import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  VehicleViewProps,
  AreaPosition,
  PaintStatus,
} from './VehiclePaintMeasurement.types';
import { SedanSVG } from '../VehicleGapDetection/SedanSVG';
import { VanSVG } from '../VehicleGapDetection/VanSVG';
import { HatchbackSVG } from '../VehicleGapDetection/HatchbackSVG';
import { PaintAreaMarker } from './PaintAreaMarker';
import { getVehicleAreasMap } from './areasMapping';
import { getVehicleSvgSize, getVehicleSvgStyle } from '../shared/vehicleSvgSizeMap';
import clsx from 'clsx';

/**
 * Komponent pojedynczego widoku pojazdu z obszarami pomiaru lakieru
 *
 * Logika działania:
 * 1. Pobiera mapowanie obszarów dla danego typu pojazdu
 * 2. Iteruje po wszystkich możliwych pozycjach
 * 3. Pokazuje obszar TYLKO jeśli ma zdefiniowaną definicję (komponent SVG)
 * 4. Status, kolor i wartość pobiera z API (props.areas)
 * 5. Jeśli brak danych z API, używa domyślnego statusu 'MISSING'
 */
export const VehicleView: React.FC<VehicleViewProps> = ({
  view,
  vehicleType,
  areas,
  label,
  className,
  scale,
  isMobile = false,
  highlightedArea = null,
  onAreaHover,
  onAreaClick,
}) => {
  const VehicleSVG =
    vehicleType === 'sedan'
      ? SedanSVG
      : vehicleType === 'van'
        ? VanSVG
        : HatchbackSVG;

  // Mierzymy faktyczną szerokość kontenera SVG i porównujemy z wartością bazową
  // (designWidth = maxWidth z vehicleSvgSizeMap). Markery skalują się proporcjonalnie,
  // gdy kontener jest węższy niż design (mobile w wąskim swiperze itp.).
  const containerRef = useRef<HTMLDivElement>(null);
  const designWidth = getVehicleSvgSize(vehicleType, view).maxWidth;
  const [autoScale, setAutoScale] = useState(1);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.offsetWidth;
      if (w > 0) {
        setAutoScale(Math.min(1, w / designWidth));
      }
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [designWidth]);

  // Pobierz mapowanie obszarów dla tego typu pojazdu
  const areasMap = useMemo(() => getVehicleAreasMap(vehicleType), [vehicleType]);

  // Utwórz mapę nameId -> dane z API dla szybkiego wyszukiwania
  const areaDataMap = useMemo(() => {
    const map = new Map<
      AreaPosition,
      { status: PaintStatus; value?: number; minMeasurement?: number; maxMeasurement?: number }
    >();
    areas.forEach((area) => {
      map.set(area.nameId, {
        status: area.status,
        value: area.value,
        minMeasurement: area.minMeasurement,
        maxMeasurement: area.maxMeasurement,
      });
    });
    return map;
  }, [areas]);

  // Filtruj obszary do wyświetlenia:
  // - muszę mieć zdefiniowaną definicję (komponent SVG)
  // - dla tego widoku (left/top/right)
  // - iterujemy po wszystkich kluczach z areasMap[view], nie tylko 1-11
  const areasToDisplay = useMemo(() => {
    const viewMapping = areasMap[view];

    // Iteruj po wszystkich kluczach zdefiniowanych w areasMapping dla tego widoku
    return Object.entries(viewMapping)
      .map(([position, areaDefinition]) => {
        // Pomijamy obszar jeśli nie ma definicji
        if (!areaDefinition) return null;

        // Pobierz dane z API lub użyj domyślnych
        // Normalizuj position do stringa dla porównania
        const normalizedPosition = String(position);
        const areaData = areaDataMap.get(normalizedPosition);
        const status = areaData?.status || 'MISSING';
        const value = areaData?.value;

        return {
          position: normalizedPosition,
          areaDefinition,
          status,
          value,
          minMeasurement: areaData?.minMeasurement,
          maxMeasurement: areaData?.maxMeasurement,
        };
      })
      .filter((area) => area !== null);
  }, [areasMap, view, areaDataMap]);

  return (
    <div className="flex flex-col gap-3 self-stretch">
      {/* Etykieta widoku */}
      <div className={clsx('text-xs font-normal text-blue-300 ', label?.length > 0 ? 'pt-0' : 'pt-4', {
        'text-left  pl-10 lg:pl-0': view === 'left',
        'text-right pr-10 lg:pr-0': view === 'right',
      })}>{label}</div>

      {/* SVG pojazdu z obszarami */}
      <div ref={containerRef} className={clsx(className, "vehicle-svg-container overflow-visible relative mx-auto w-full")} style={getVehicleSvgStyle(vehicleType, view)}>
        <div className="overflow-visible relative h-full origin-top">
          {/* Sylwetka pojazdu */}
          <VehicleSVG view={view} className="w-full h-full" />

          {/* Overlay z obszarami lakieru */}
          <div className={clsx(`overflow-visible absolute inset-0 z-20 w-full h-full pointer-events-none`, scale)}>
            {areasToDisplay.map((area) => (
              <PaintAreaMarker
                key={area.position}
                position={area.position}
                status={area.status}
                value={area.value}
                areaDefinition={area.areaDefinition}
                minMeasurement={area.minMeasurement}
                maxMeasurement={area.maxMeasurement}
                isMobile={isMobile}
                autoScale={autoScale}
                isHighlighted={highlightedArea === area.position}
                onHover={onAreaHover}
                onClick={onAreaClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
